/**
 *
 * @module configurationClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/configuration.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Util from './../util/entry';
import Exception from './../exception/entry';
import HttpClient from './../platforms/httpClient';
import EnvironmentConfiguration from './../platforms/environmentConfiguration';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';

import HttpMethod from './httpMethod';
import ConfigurationClientConfiguration from './configurationClientConfiguration';
import SdkSessionConfiguration from './sdkSessionConfiguration';
import BootstrapConfiguration from './bootstrapConfiguration';

const ConfigurationClientDustReference = DustUrnReference.services.configuration.configurationClient;

const ServiceException = Exception.ServiceException;
const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class ConfigurationClient
 * @implements {IConfigurationClient}
 * @desc Provides a data client that can be used to access configuration services.
 *
 */
export default class ConfigurationClient {

    /**
     *
     * @param {ConfigurationClientConfiguration} configurationClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(configurationClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'ConfigurationClient(configurationClientConfiguration, logger, httpClient)',
            get configurationClientConfiguration() {
                return `${this.ctor} needs ConfigurationClientConfiguration instance`;
            }
        };

        instanceStrict(
            configurationClientConfiguration,
            ConfigurationClientConfiguration,
            errorMessage.configurationClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {ConfigurationClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = configurationClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(ConfigurationClient.name, 'Created.');

    }

    /**
     *
     * @param {BootstrapConfiguration} bootstrapConfiguration - The bootstrap configuration
     * information used to construct the service request.
     * @param {EnvironmentConfiguration} environmentConfiguration - The environment configuration
     * information used to construct the service request.
     * @desc Retrieves the SdkSessionConfiguration information necessary to create an SdkSession instance.
     * @returns {Promise<SdkSessionConfiguration>} The configuration information.
     *
     */
    getConfiguration(bootstrapConfiguration, environmentConfiguration) {

        const errorMessage = {
            method: 'ConfigurationClient.getConfiguration(bootstrapConfiguration, environmentConfiguration)',
            get bootstrapConfiguration() {
                return `${this.method} needs BootstrapConfiguration instance`;
            },
            get environmentConfiguration() {
                return `${this.method} needs EnvironmentConfiguration instance`;
            }
        };

        instanceStrict(
            bootstrapConfiguration,
            BootstrapConfiguration,
            errorMessage.bootstrapConfiguration,
            InvalidArgumentException
        );

        instanceStrict(
            environmentConfiguration,
            EnvironmentConfiguration,
            errorMessage.environmentConfiguration,
            InvalidArgumentException
        );

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(bootstrapConfiguration, environmentConfiguration);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ConfigurationClient.name,
            urn: ConfigurationClientDustReference.getConfiguration,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { commonHeaders, services } = data;

            return Promise.resolve(
                new SdkSessionConfiguration(
                    commonHeaders,
                    data.logger,
                    services,
                    environmentConfiguration,
                    logger
                )
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {BootstrapConfiguration} bootstrapConfiguration
     * @param {EnvironmentConfiguration} environmentConfiguration
     * @returns {Object} payload
     *
     */
    getPayload(bootstrapConfiguration, environmentConfiguration) {

        const { environment, clientId, configHostName } = bootstrapConfiguration;
        const { sdkVersion, deviceFamily, deviceProfile, applicationRuntime, configVersion } = environmentConfiguration;
        const { endpoints } = this.config;

        const endpoint = endpoints.bootstrap;
        const headers = endpoint.headers;

        let href = endpoint.href;

        if (endpoint.templated) {

            const baseUrl = Util.getSafe(() => this.config.extras.configHostParams[configHostName].baseUrl);

            href = href
                .replace(/\{baseUrl\}/gi, baseUrl)
                .replace(/\{configVersion\}/gi, configVersion)
                .replace(/\{clientId\}/gi, clientId)
                .replace(/\{deviceFamily\}/gi, deviceFamily)
                .replace(/\{sdkVersion\}/gi, sdkVersion)
                .replace(/\{applicationRuntime\}/gi, applicationRuntime)
                .replace(/\{deviceProfile\}/gi, deviceProfile)
                .replace(/\{environment\}/gi, environment);
        }

        return {
            url: href,
            headers
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/configuration/configurationClient.js